import React, { memo, useCallback, useContext, MouseEvent } from 'react'
import { HorizontalDotsMinor, ReplayMinor } from '@shopify/polaris-icons'
import { Payment } from 'src/interfaces/payment'
import { Button } from 'src/components/shared/Button'
import { PaymentStatus } from 'src/constants/payment/enums'
import { colors } from 'src/theme'
import { useDispatch } from 'react-redux'
import { createAsyncAction } from 'src/utils/reduxUtils'
import { ProcessPayment } from 'src/constants/actionTypes'
import { showToast } from 'src/utils/toast'
import { Tooltip, Typography } from '@mui/material'
import tw from 'twin.macro'
import { ReloadWrapperContext } from 'src/components/wrapper/reload-wrapper/ReloadWrapper.context'
import { PaymentsTableContext } from '../../payments-table/PaymentsTable.context'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'

interface Props {
  payment: Payment
}

const statusesWithActions = [
  PaymentStatus.Paid,
  PaymentStatus.NotPaid,
  PaymentStatus.RequiresFunding,
]

const PaymentsItemActionButtonComponent = ({ payment }: Props) => {
  const dispatch = useDispatch()

  const { hasPermission } = useCheckPermissions()

  const { reload } = useContext(ReloadWrapperContext)
  const { openPaymentsItemAction } = useContext(PaymentsTableContext)

  const handleClickAction = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      openPaymentsItemAction(event, payment)
    },
    [payment],
  )

  const handleClickRetry = useCallback(async () => {
    try {
      const processedPayment: Payment = await createAsyncAction(
        dispatch,
        ProcessPayment.request({ paymentId: payment.id }),
      )

      reload()

      if (processedPayment.status === PaymentStatus.Failed) {
        throw processedPayment.failureReason
      }

      if (processedPayment.status === PaymentStatus.Pending) {
        showToast('Processing your payment', { variant: 'warning' })

        return
      }

      showToast('Payment have proceeded', { variant: 'success' })
    } catch {
      showToast(
        <Typography variant="body2">
          This payment cannot be retried. Please email{' '}
          <a
            href="mailto:support@gigsafe.com"
            style={{
              color: 'inherit',
              textDecoration: 'underline',
              fontWeight: 500,
            }}
          >
            support@gigsafe.com
          </a>{' '}
          for the payment to be reuploaded and processed for you.
        </Typography>,
        {
          variant: 'error',
          autoHideDuration: 10000,
        },
      )
    }
  }, [payment, reload])

  if (payment.status === PaymentStatus.Failed && hasPermission(UserPermission.PaymentsProcess)) {
    return (
      <Tooltip color="black" title="Retry payment" placement="left-end">
        <span css={tw`min-w-0 inline-flex`}>
          <Button
            color="light"
            size="tiny"
            leftIcon={<ReplayMinor fill={colors.PRIMARY_PURPLE_500} width={20} height={20} />}
            onClick={handleClickRetry}
            disabled={!payment.isRetryable}
          />
        </span>
      </Tooltip>
    )
  }

  return (
    <Button
      color="white"
      size="tiny"
      disabled={!statusesWithActions.includes(payment.status) && !payment.isCancelable}
      leftIcon={<HorizontalDotsMinor width={20} height={20} />}
      onClick={handleClickAction}
    />
  )
}

export const PaymentsItemActionButton = memo(PaymentsItemActionButtonComponent)
